import React, {memo, useCallback, FC} from "react";
import {useDispatch, useSelector} from "react-redux";
import {sideMenuSelector} from "./sideMenuSelector";
import {uiActions} from "../../ui/ui.slice";
import {reviewActions} from "../review.slice";
import styled from "styled-components";
import { ThemeProps } from "../../styles/theme";
import { Link } from "@reach/router";
import { useTheme } from "../../styles/ThemeContext";
import { RootState } from "../../store/rootReducer";

const Overlay = styled.div<{$isHidden: boolean}>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    opacity: ${props => props.$isHidden ? 0 : 1};
    visibility: ${props => props.$isHidden ? 'hidden' : 'visible'};
    transition: opacity 0.3s ease, visibility 0.3s ease;
    z-index: 1;
`;

const Root = styled.div<{$isHidden: boolean}>`
    position: fixed;
    left: 0;
    top: 0;
    height: 100%;
    background-color: ${props => props.theme.primaryBackground};
    border-right: ${props => props.theme.primaryBorder} 1px solid;
    transition: transform 0.3s ease;
    width: 250px;
    transform: translateX(${props => props.$isHidden ? '-100%' : '0'});
    z-index: 2;
    overflow: hidden;
`;

const MenuHeader = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 10px;
    border-bottom: 1px solid ${props => props.theme.primaryBorder};
`;

const CloseButton = styled.button<ThemeProps>`
    background: none;
    border: none;
    color: ${props => props.theme.secondaryIcon};
    cursor: pointer;
    padding: 5px;
    font-size: 1.2em;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: ${props => props.theme.primaryForeground};
    }
`;

const MenuContent = styled.div<ThemeProps>`
    color: ${props => props.theme.primaryForeground};
    width: 250px;
    box-sizing: border-box;
    height: 100%;
`;

const ContentSection = styled.div`
    padding: 20px 20px 0 20px;
    height: calc(100% - 46px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    
    &::after {
        content: '';
        display: block;
        height: 40px;
        width: 100%;
    }
`;

const MenuSection = styled.div`
    margin-bottom: 20px;
`;

const SectionTitle = styled.h3<ThemeProps>`
    font-size: 1em;
    color: ${props => props.theme.secondaryIcon};
    margin: 0 0 10px 0;
    padding-bottom: 5px;
    border-bottom: 1px solid ${props => props.theme.primaryBorder};
`;

const StyledLink = styled.a<ThemeProps>`
    display: block;
    padding: 8px 0;
    color: ${props => props.theme.primaryForeground};
    text-decoration: none;
    font-size: 0.9em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
        color: ${props => props.theme.secondaryIcon};
    }
`;

const ThemeButtonGroup = styled.div`
    display: flex;
    gap: 4px;
    padding: 8px 0;
`;

const ThemeButton = styled.button<ThemeProps & { $active: boolean }>`
    flex: 1;
    background: ${props => props.$active ? props.theme.secondaryBackground : 'none'};
    border: 1px solid ${props => props.$active ? props.theme.secondaryIcon : props.theme.primaryBorder};
    border-radius: 4px;
    color: ${props => props.$active ? props.theme.secondaryIcon : props.theme.primaryForeground};
    font-size: 0.9em;
    cursor: pointer;
    padding: 4px;
    transition: all 0.2s ease;

    &:hover {
        background: ${props => props.theme.secondaryBackground};
        border-color: ${props => props.theme.secondaryIcon};
    }
`;

const MenuItem = (props: { to: string; children: React.ReactNode }) => {
    const dispatch = useDispatch();
    const closeMenu = useCallback(() => {
        dispatch(uiActions.closeSideMenu());
    }, [dispatch]);

    return (
        <Link to={props.to} style={{ textDecoration: 'none', display: 'block' }} onClick={closeMenu}>
            <StyledLink as="span">{props.children}</StyledLink>
        </Link>
    );
};

const MenuActionItem = (props: { onClick: () => void; children: React.ReactNode }) => {
    const dispatch = useDispatch();
    const handleClick = useCallback(() => {
        props.onClick();
        dispatch(uiActions.closeSideMenu());
    }, [dispatch, props]);

    return (
        <StyledLink as="span" role="button" style={{ cursor: 'pointer' }} onClick={handleClick}>
            {props.children}
        </StyledLink>
    );
};

const ThemeSelector: FC = () => {
    const { themeMode, setThemeMode } = useTheme();
    
    return (
        <MenuSection>
            <SectionTitle>Theme</SectionTitle>
            <ThemeButtonGroup>
                <ThemeButton 
                    onClick={() => setThemeMode('auto')} 
                    $active={themeMode === 'auto'}
                    title="System Theme"
                >
                    Auto
                </ThemeButton>
                <ThemeButton 
                    onClick={() => setThemeMode('light')} 
                    $active={themeMode === 'light'}
                    title="Light Theme"
                >
                    Light
                </ThemeButton>
                <ThemeButton 
                    onClick={() => setThemeMode('dark')} 
                    $active={themeMode === 'dark'}
                    title="Dark Theme"
                >
                    Dark
                </ThemeButton>
            </ThemeButtonGroup>
        </MenuSection>
    );
};

export const SideMenu: FC = memo(() => {
    const isSideMenuOpen = useSelector((state: RootState) => state.ui.isSideMenuOpen);
    const stateProps = useSelector(sideMenuSelector);
    const dispatch = useDispatch();
    
    const closeMenu = useCallback(() => {
        dispatch(uiActions.closeSideMenu());
    }, [dispatch]);

    const startReview = useCallback(() => {
        dispatch(reviewActions.loadNext());
    }, [dispatch]);

    const handleMenuClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    return (
        <>
            <Overlay $isHidden={!isSideMenuOpen} onClick={closeMenu} />
            <Root $isHidden={!isSideMenuOpen} onClick={handleMenuClick}>
                <MenuContent>
                    <MenuHeader>
                        <CloseButton onClick={closeMenu}>✕</CloseButton>
                    </MenuHeader>
                    <ContentSection>
                        <MenuSection>
                            <SectionTitle>Study Options</SectionTitle>
                            <MenuActionItem onClick={startReview}>Review Cards</MenuActionItem>
                            <MenuItem to="/car">Car Mode</MenuItem>
                            <MenuItem to="/memorize">Memorize Cards</MenuItem>
                            <MenuItem to="/search">Search Cards</MenuItem>
                            <MenuItem to="/print">Print Cards</MenuItem>
                        </MenuSection>
                        <MenuSection>
                            <SectionTitle>Card Management</SectionTitle>
                            <MenuItem to="/add">Add New Card</MenuItem>
                            <MenuItem to="/quick-add">Quick Add</MenuItem>
                            {typeof stateProps.cardId === 'string' && stateProps.cardId.length > 0 && (
                                <>
                                    <MenuItem to={`/edit/${stateProps.cardId}`}>Edit Current Card</MenuItem>
                                    <MenuItem to={`/card/${stateProps.cardId}`}>View Current Card</MenuItem>
                                </>
                            )}
                        </MenuSection>
                        <MenuSection>
                            <SectionTitle>Diagnostics</SectionTitle>
                            <MenuItem to="/llm-log">LLM Log</MenuItem>
                        </MenuSection>
                        <MenuSection>
                            <SectionTitle>Help</SectionTitle>
                            <MenuItem to="/help/markup">Markup Reference</MenuItem>
                        </MenuSection>
                        <ThemeSelector />
                    </ContentSection>
                </MenuContent>
            </Root>
        </>
    );
});